/**
 * The four parts of the subcontractor packet, in the order they are signed:
 * the W-9, the bank account, insurance, and the agreement.
 */

import { Box, Chips, Pick, Tick, textOf, type PacketForm } from "./fields";
import type { PacketLanguage } from "./words";

interface AgreementBlock {
  kind: "heading" | "clause" | "item" | "para";
  number?: string;
  title?: string;
  text?: string;
}

export interface AgreementText {
  title: string;
  intro: string;
  consideration: string;
  blocks: AgreementBlock[];
  witness: string;
  languageNote: string;
}

/** What the `open` call hands the page. Never a number. */
export interface OpenPacket {
  crew: {
    name: string | null;
    contactName: string | null;
    email: string | null;
    phone: string | null;
  };
  company: string;
  /**
   * Who signs for the company. `titles` is the title in each language, as the
   * signed PDF prints it; `title` is the English, for a function deployed
   * before `titles` was sent.
   */
  companySigner: {
    name: string;
    title: string;
    titles?: Record<PacketLanguage, string>;
  };
  certificate: { email: string; holder: string; holderAddress: string };
  agreement: Record<PacketLanguage, AgreementText>;
}

export function W9Section({
  form,
  language,
}: {
  form: PacketForm;
  language: PacketLanguage;
}) {
  const { words } = form;
  const taxClass = textOf(form, "taxClass");
  const tinKind = textOf(form, "tinKind");
  return (
    <section className="cpk-section" aria-labelledby="cpk-w9">
      <h2 id="cpk-w9">{words.w9Title}</h2>
      <p className="cpk-hint">{words.w9Hint}</p>
      <Box
        form={form}
        field="legalName"
        label={words.legalName}
        hint={words.legalNameHint}
        autoComplete="name"
      />
      <Box
        form={form}
        field="businessName"
        label={words.businessName}
        autoComplete="organization"
      />
      <Pick
        form={form}
        field="taxClass"
        label={words.taxClass}
        options={words.taxClasses}
      />
      {taxClass === "llc" ? (
        <Pick
          form={form}
          field="llcCode"
          label={words.llcCode}
          options={words.llcCodes}
        />
      ) : null}
      {taxClass === "other" ? (
        <Box form={form} field="otherClass" label={words.otherClass} />
      ) : null}
      <Box
        form={form}
        field="street"
        label={words.street}
        autoComplete="street-address"
      />
      <Box
        form={form}
        field="city"
        label={words.city}
        autoComplete="address-level2"
      />
      <div className="ilink-pair">
        <Box
          form={form}
          field="state"
          label={words.state}
          autoComplete="address-level1"
          maxLength={2}
        />
        <Box
          form={form}
          field="zip"
          label={words.zip}
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={10}
        />
      </div>
      <Chips
        form={form}
        field="tinKind"
        question={words.tinKind}
        choices={[
          { value: "ssn", label: words.ssn },
          { value: "ein", label: words.ein },
        ]}
      />
      {tinKind === "ssn" || tinKind === "ein" ? (
        <Box
          form={form}
          field="tin"
          label={words[tinKind]}
          inputMode="numeric"
          placeholder={words.tinPlaceholder[tinKind]}
          maxLength={11}
        />
      ) : null}
      <Tick form={form} field="backupWithholding" label={words.backup} />
      <p className="cpk-hint">
        {words.certification}{" "}
        <a
          href={language === "es" ? "/forms/fw9sp.pdf" : "/forms/fw9.pdf"}
          target="_blank"
          rel="noreferrer"
        >
          {words.viewForm}
        </a>
      </p>
    </section>
  );
}

export function BankSection({ form }: { form: PacketForm }) {
  const { words } = form;
  return (
    <section className="cpk-section" aria-labelledby="cpk-bank">
      <h2 id="cpk-bank">{words.bankTitle}</h2>
      <p className="cpk-hint">{words.bankHint}</p>
      <Box form={form} field="bankName" label={words.bankName} />
      <Box form={form} field="accountName" label={words.accountName} />
      <Box
        form={form}
        field="routingNumber"
        label={words.routingNumber}
        hint={words.routingHint}
        inputMode="numeric"
        maxLength={9}
      />
      <Box
        form={form}
        field="accountNumber"
        label={words.accountNumber}
        inputMode="numeric"
        maxLength={17}
      />
      <Box
        form={form}
        field="accountNumberAgain"
        label={words.accountNumberAgain}
        inputMode="numeric"
        maxLength={17}
      />
    </section>
  );
}

export function InsuranceSection({
  form,
  packet,
}: {
  form: PacketForm;
  packet: OpenPacket;
}) {
  const { words } = form;
  const yesNo = [
    { value: true, label: words.yes },
    { value: false, label: words.no },
  ];
  return (
    <section className="cpk-section" aria-labelledby="cpk-insurance">
      <h2 id="cpk-insurance">{words.insuranceTitle}</h2>
      <Chips form={form} field="hasWc" question={words.hasWc} choices={yesNo} />
      <Chips form={form} field="hasGl" question={words.hasGl} choices={yesNo} />
      <div className="cpk-note">
        <b>{words.certificateTitle}</b>
        <p>
          {words.certificate(
            packet.certificate.email,
            packet.certificate.holder,
            packet.certificate.holderAddress,
          )}
        </p>
      </div>
    </section>
  );
}

function AgreementReader({ agreement }: { agreement: AgreementText }) {
  return (
    <div className="cpk-agreement" tabIndex={0}>
      <h3>{agreement.title}</h3>
      <p>{agreement.intro}</p>
      <p>{agreement.consideration}</p>
      {agreement.blocks.map((block, index) =>
        block.kind === "heading" ? (
          <p key={index} className="cpk-article">
            <b>
              {block.number}: {block.title}
            </b>{" "}
            {block.text}
          </p>
        ) : (
          <p
            key={index}
            className={block.kind === "item" ? "cpk-item" : "cpk-clause"}
          >
            {block.kind === "clause" ? <b>{block.number}</b> : block.number}{" "}
            {block.text}
          </p>
        ),
      )}
      <p>{agreement.witness}</p>
      <p>
        <i>{agreement.languageNote}</i>
      </p>
    </div>
  );
}

export function AgreementSection({
  form,
  packet,
  language,
  onLanguage,
}: {
  form: PacketForm;
  packet: OpenPacket;
  language: PacketLanguage;
  onLanguage: (language: PacketLanguage) => void;
}) {
  const { words } = form;
  const other = language === "en" ? "es" : "en";
  return (
    <section className="cpk-section" aria-labelledby="cpk-agreement">
      <h2 id="cpk-agreement">{words.agreementTitle}</h2>
      <p className="cpk-hint">
        {words.agreementHint}{" "}
        <button
          type="button"
          className="cpk-link"
          lang={other}
          onClick={() => onLanguage(other)}
        >
          {words.agreementOther}
        </button>
      </p>
      <AgreementReader agreement={packet.agreement[language]} />
      <Box
        form={form}
        field="phone"
        label={words.phone}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
      />
      <Box
        form={form}
        field="email"
        label={words.email}
        type="email"
        inputMode="email"
        autoComplete="email"
      />
      <Box
        form={form}
        field="signerName"
        label={words.signerName}
        autoComplete="name"
      />
      <Box
        form={form}
        field="signerTitle"
        label={words.signerTitle}
        hint={words.signerTitleHint}
        autoComplete="organization-title"
      />
      <p className="cpk-hint">
        {words.companySigns(
          packet.companySigner.name,
          packet.companySigner.titles?.[language] ?? packet.companySigner.title,
          packet.company,
        )}
      </p>
      <Tick form={form} field="consented" label={words.consent} />
    </section>
  );
}
